import { useState, useMemo } from 'react';
import { Variable } from 'api/data/variables';
import {
	FormCard,
	DeleteFormModal,
	NoForms,
	CreateFormModal,
	RenameFormModal,
	FormsSection
} from 'components/Forms';
import { FormsFilterShowOptions } from 'store/data/forms';
import { Select } from 'components/UI/Interactables/Select';
import { Header } from 'components/Header';
import { Flex } from 'components/UI/Flex';
import { Gap } from 'components/UI/Gap';
import { Grid } from 'components/UI/Grid';
import { InfoMessage } from 'components/UI/InfoMessage';
import { SearchInput } from 'components/UI/Inputs/SearchInput';
import { Button } from 'components/UI/Interactables/Button';
import { Spacer } from 'components/UI/Spacer';
import { Suspend } from 'components/UI/Suspend';
import { Typography } from 'components/UI/Typography';
import { filterFormsDataBySearchTerm } from 'helpers/forms';
import { variablesOrderIterator } from 'helpers/variables';
import { useNavigation } from 'hooks/navigation';
import {
	useTranslation,
	useScopedProjects,
	useFormId,
	useProjectId,
	useVariables,
	useVariablesData,
	usePermissions,
	useForms,
	useFormsFilters,
	useFormsSectionCollapsed,
	useFormsBySetName,
	useMoveForm
} from 'hooks/store';
import { DragAndDropProvider, DroppableArea } from 'features/dataset-page/DragAndDropContext';
import { openInNewTab } from 'features/communication-bridge/openInNewTab';

export function FormsPage() {
	const { routes, navigate } = useNavigation();

	const { translate } = useTranslation();
	const [{ loading: loadingProjects }] = useScopedProjects({
		lazy: true
	});

	const [, setFormId] = useFormId();
	const [projectId] = useProjectId();

	const [, moveForm] = useMoveForm();

	const [
		{
			data: { variablesMap, groupsMap, variableSetsMap, variablesOutsideSets },
			loading: loadingVariables,
			fetched: areVariablesFetched
		}
	] = useVariables({ initial: true });
	const variablesData = useVariablesData({ initial: true });

	const { hasVariablesWriteAccess } = usePermissions();

	const [{ data: forms, loading: loadingForms, fetched: areFormsFetched }] = useForms();

	const [{ filters }, { setShowFilter }] = useFormsFilters();
	const [sectionCollapsedMap, toggleSectionCollapsed] = useFormsSectionCollapsed();

	const [createFormModalVisible, setCreateFormModalVisible] = useState(false);
	const [renameFormModalVisible, setRenameFormModalVisible] = useState(false);
	const [deleteFormModalVisible, setDeleteFormModalVisible] = useState(false);

	const [selectedFormId, setSelectedFormId] = useState<string | null>(null);

	// TODO: move in redux maybe
	const [searchTerm, setSearchTerm] = useState('');
	const isSearchTermValid = searchTerm.trim().length > 0;

	const formsBySetName = useFormsBySetName();

	const filteredFormsData = useMemo(
		() => filterFormsDataBySearchTerm({ forms, formsBySetName }, searchTerm),
		[forms, formsBySetName, searchTerm]
	);

	function handleFormRename(formId: string) {
		setSelectedFormId(formId);
		setRenameFormModalVisible(true);
	}

	function handleFormDelete(formId: string) {
		setSelectedFormId(formId);
		setDeleteFormModalVisible(true);
	}

	function isSectionVisible(filterShowOption: FormsFilterShowOptions) {
		return [FormsFilterShowOptions.ALL, filterShowOption].includes(filters.show);
	}

	function handleFormClick(formId: string, newTab?: boolean) {
		if (projectId) {
			if (newTab) {
				openInNewTab(routes.projects.forms.form(projectId, formId));

				return;
			}

			setFormId(formId);
			navigate(routes.projects.forms.form(projectId, formId));
		}
	}

	const mainSection = {
		label: translate(dict => dict.formDesigner.mainSection),
		value: 'main_section'
	};

	const setForms = Object.values(formsBySetName).flat();
	const hasForms = forms.length > 0 || setForms.length > 0;

	const loadingFormsInitial = loadingForms && !areFormsFetched;
	const suspendProps = {
		loading: loadingFormsInitial || loadingVariables || loadingProjects,
		immediate: !areFormsFetched || !areVariablesFetched
	};

	const setScopeVariablesMap = useMemo(() => {
		const setScopeVariablesMap: Record<string, Variable[]> = {};

		for (const setName in variableSetsMap) {
			const variableSet = variableSetsMap[setName];

			const setVariables: Variable[] = [];

			variablesOrderIterator(
				variableSet.setOrder,
				variableName => setVariables.push(variablesMap[variableName]),
				groupName => {
					const group = groupsMap[groupName];

					group.variablesBelongingToGroup.forEach(variableName =>
						setVariables.push(variablesMap[variableName])
					);
				},
				// VARIABLE SET - OMIT
				() => null
			);

			setScopeVariablesMap[setName] = setVariables;
		}

		return setScopeVariablesMap;
	}, [variableSetsMap]);

	// Unified sections data structure
	const allFormsSections = [
		// Add main section if visible
		...(isSectionVisible(FormsFilterShowOptions.MAIN)
			? [
					{
						key: mainSection.value,
						title: mainSection.label,
						sectionName: mainSection.value,
						forms: filteredFormsData.forms,
						variables: variablesOutsideSets
					}
			  ]
			: []),

		// Add variable set sections if visible
		...(isSectionVisible(FormsFilterShowOptions.VARIABLE_SETS)
			? Object.entries(filteredFormsData.formsBySetName)
					.filter(([, forms]) => forms.length > 0)
					.map(([setName, forms]) => ({
						key: setName,
						title: variableSetsMap[setName]?.setLabel || setName,
						sectionName: setName,
						forms,
						variables: setScopeVariablesMap[setName] || []
					}))
			: [])
	];

	return (
		<>
			<Header.Main />
			<Header.Navigation
				rightComponent={({ addButton }) =>
					!suspendProps.immediate &&
					hasVariablesWriteAccess &&
					hasForms && (
						<Flex>
							<SearchInput
								usedInHeader
								placeholder={translate(dict => dict.formDesigner.searchForms)}
								term={searchTerm}
								onChangeTerm={term => term !== searchTerm && setSearchTerm(term)}
							/>

							{addButton({
								label: translate(dict => dict.formDesigner.createNewForm),
								action: () => setCreateFormModalVisible(true)
							})}
						</Flex>
					)
				}
			/>
			<Header.Title
				title={translate(dict => dict.projectTabs.forms)}
				component={
					<Flex>
						{/* CLEAR FILTERS */}
						{isSearchTermValid && (
							<>
								{hasVariablesWriteAccess && (
									<Flex align={a => a.center} marginOffset={{ x: 1.6 }}>
										<InfoMessage
											message={translate(
												dict => dict.variables.header.clearFilters.message
											)}
										/>
									</Flex>
								)}
								<Button
									title={translate(
										dict => dict.variables.header.clearFilters.button
									)}
									variant={v => v.link}
									marginOffset={{ right: 2.4 }}
									onClick={() => setSearchTerm('')}
								/>
							</>
						)}
					</Flex>
				}
			/>

			<Suspend
				loading={loadingFormsInitial || loadingVariables || loadingProjects}
				immediate={!areFormsFetched || !areVariablesFetched}
			>
				<Grid.Container>
					{/* NO FORMS */}
					{!hasForms && <NoForms onCreateClick={() => setCreateFormModalVisible(true)} />}

					{/* FORMS LIST */}
					{hasForms && (
						<DragAndDropProvider isEnabled>
							<Flex align={a => a.center}>
								<Flex>
									<Typography.Caption marginOffset={{ right: 0.8 }}>
										{translate(dict => dict.dataset.seriesEntryForm.show)}
									</Typography.Caption>
									<Select
										type={t => t.Tag}
										title={translate(
											dict => dict.formDesigner.showOptions[filters.show]
										)}
										width={12}
										items={Object.values(FormsFilterShowOptions).map(value => ({
											label: translate(
												dict => dict.formDesigner.showOptions[value]
											),
											value
										}))}
										onSelect={item =>
											setShowFilter(item.value as FormsFilterShowOptions)
										}
									/>
								</Flex>
							</Flex>
							<Spacer size={s => s.xs} />
							{/* UNIFIED FORMS SECTIONS */}
							{allFormsSections.map((section, sectionIndex) => (
								<div key={section.key}>
									<FormsSection
										title={section.title}
										forms={section.forms}
										open={!sectionCollapsedMap[section.sectionName]}
										onToggle={() =>
											toggleSectionCollapsed({
												sectionName: section.sectionName
											})
										}
										renderFormCard={(form, index) => {
											const handleDrop = (draggableId: string) => {
												const sourceIndex = section.forms.findIndex(
													f => f.id === draggableId
												);

												if (sourceIndex === index) {
													return;
												}

												moveForm({
													formId: draggableId,
													sourceIndex,
													destinationIndex: index
												});
											};

											return (
												<div className="flex flex-row" key={form.id}>
													<DroppableArea
														includeIds={section.forms
															.map(f => f.id)
															.filter(id => id !== form.id)
															.filter(
																id =>
																	id !==
																	section.forms[index - 1]?.id
															)}
														className="h-full w-2 rounded-sm"
														onDrop={draggableId => {
															handleDrop(draggableId);
														}}
													/>

													<FormCard
														form={form}
														variables={section.variables}
														variablesData={variablesData}
														onClick={handleFormClick}
														onRename={handleFormRename}
														onDelete={handleFormDelete}
													/>

													{index === section.forms.length - 1 && (
														<DroppableArea
															includeIds={section.forms
																.map(f => f.id)
																.filter(id => id !== form.id)}
															className="h-full w-2 rounded-sm"
															onDrop={draggableId => {
																handleDrop(draggableId);
															}}
														/>
													)}
												</div>
											);
										}}
									/>

									{sectionIndex < allFormsSections.length - 1 ? (
										<Gap marginGap={{ bottom: 0.8 }} />
									) : null}
								</div>
							))}
						</DragAndDropProvider>
					)}
				</Grid.Container>
			</Suspend>

			{/*
				=============
					MODALS
				=============
			*/}

			{/* CREATE FORM MODAL */}
			{createFormModalVisible && (
				<CreateFormModal onClose={() => setCreateFormModalVisible(false)} />
			)}

			{/* RENAME FORM MODAL */}
			{renameFormModalVisible && selectedFormId !== null && (
				<RenameFormModal
					formId={selectedFormId}
					onClose={() => setRenameFormModalVisible(false)}
				/>
			)}

			{/* DELETE FORM MODAL */}
			{deleteFormModalVisible && selectedFormId !== null && (
				<DeleteFormModal
					formId={selectedFormId}
					onClose={() => setDeleteFormModalVisible(false)}
				/>
			)}
		</>
	);
}
